export const EMAIL_SETTINGS = {
    DEFAULT_NO_REPLY: { name: 'Curium', email: 'no-reply@curium.app' },
    DEFAULT_FROM: { name: 'Curium', email: 'info@curium.app' },
    PLATFORM_SENDER: { name: 'Curium', email: 'app@curium.app' },
    REPORTS_SENDER: { name: 'Curium', email: 'reports@curium.app' },
    MAX_ATTACHMENT_SIZE_IN_BYTES: 26214400,
} as const;

/**
 * RFC 3834 header Curium sets on its own automated reports. Receivers use it to
 * suppress auto-replies, and the AI inbox uses it, together with the reports
 * sender, to keep Curium's digests out of classification.
 */
export const AUTOMATED_REPORT_HEADERS = { 'Auto-Submitted': 'auto-generated' } as const satisfies Record<
    string,
    string
>;

/**
 * Domains authenticated for outbound SMTP via SendGrid. Sender-trust decisions
 * (i.e. "can we send mail FROM this domain without per-org SendGrid setup?")
 * MUST use exact match against this list — NO suffix matching. SendGrid does
 * not authenticate subdomains transitively, so a custom sender on
 * `foo.curium.app` is NOT trusted just because `curium.app` is on the list.
 */
export const SYSTEM_SENDER_DOMAINS = ['curium.app', 'getcurium.com'] as const;

export type SystemSenderDomain = (typeof SYSTEM_SENDER_DOMAINS)[number];

/**
 * System-controlled domains we own (sender-authenticated domains plus infra
 * subdomains we control — MX, link-tracking, return-path, inbound parse).
 * `isSystemDomain` matches by suffix, so any subdomain of an entry is also
 * considered a system domain. Use this for "is this domain part of our
 * infrastructure?" decisions. For "is this domain trusted to send mail?"
 * decisions, use `isSystemSenderDomain` instead.
 */
export const SYSTEM_DOMAINS = [...SYSTEM_SENDER_DOMAINS, 'sg.curium.app'] as const;

export type SystemDomain = (typeof SYSTEM_DOMAINS)[number];

/**
 * Check if a domain is a system-controlled domain (sender-authenticated OR
 * infra subdomain). Suffix-matches: `inbox.curium.app` returns true.
 *
 * For sender-trust decisions, use `isSystemSenderDomain` instead — it does
 * exact matching against the narrower `SYSTEM_SENDER_DOMAINS` list.
 */
export function isSystemDomain(domain: string): boolean {
    return SYSTEM_DOMAINS.some(
        (systemDomain) =>
            domain.toLowerCase() === systemDomain.toLowerCase() ||
            domain.toLowerCase().endsWith(`.${systemDomain.toLowerCase()}`),
    );
}

/**
 * Result of checking a custom inbound-parse hostname against this environment's
 * inbound marker (see `checkEnvInboundMarker`).
 *
 * - `match`: the hostname's leftmost label equals the env marker.
 * - `mismatch`: a marker exists for this environment but the hostname doesn't carry it.
 * - `not-applicable`: no marker can be derived (no/blank system inbound domain) — the
 *   rule MUST be skipped, never fail-closed. A missing `SYSTEM_INBOUND_DOMAIN` must not
 *   brick custom-domain saves.
 */
export type EnvInboundMarkerCheck = 'match' | 'mismatch' | 'not-applicable';

/**
 * Derive the environment-distinct inbound marker from the environment's system inbound
 * domain — its leftmost DNS label (`sg.curium.app` → `sg`, `sg-staging.curium.app` →
 * `sg-staging`, `parse-dev-jane.curium.app` → `parse-dev-jane`). Custom inbound-parse
 * subdomains are expected to carry the same marker (`sg.acme.com`) so the one shared
 * SendGrid account never sees the same custom hostname claimed by two environments.
 * Returns `null` when no marker can be derived.
 */
export function deriveEnvInboundMarker(systemInboundDomain: string | null | undefined): string | null {
    const trimmed = systemInboundDomain?.trim().toLowerCase();
    if (!trimmed) return null;
    const marker = trimmed.split('.')[0];
    return marker || null;
}

/**
 * Check whether a custom inbound-parse hostname carries this environment's inbound
 * marker as its leftmost label. Pure — callers supply the environment's system inbound
 * domain (server: `env.SYSTEM_INBOUND_DOMAIN`; client: derived from the managed inbound
 * address). Only meaningful for custom (non-system) domains — callers gate on that.
 */
export function checkEnvInboundMarker(
    hostname: string,
    systemInboundDomain: string | null | undefined,
): EnvInboundMarkerCheck {
    const marker = deriveEnvInboundMarker(systemInboundDomain);
    if (!marker) return 'not-applicable';
    const leftmostLabel = hostname.trim().toLowerCase().split('.')[0];
    return leftmostLabel === marker ? 'match' : 'mismatch';
}

/**
 * Check if a domain is authenticated for outbound SMTP via SendGrid.
 *
 * EXACT MATCH ONLY — no suffix matching. `sg.curium.app` and `foo.curium.app`
 * both return false because SendGrid does not transitively authenticate
 * subdomains.
 */
export function isSystemSenderDomain(domain: string | null | undefined): boolean {
    if (!domain) return false;
    const normalised = domain.toLowerCase();
    return SYSTEM_SENDER_DOMAINS.some((d) => d.toLowerCase() === normalised);
}

/**
 * Returns the effective authenticated domain for email sending/preview.
 * When senderEmail uses a sender-trusted system domain, authenticatedDomain is
 * ignored to prevent a stale custom domain from overriding the system-domain
 * sender.
 *
 * Sender-trust uses exact-match `isSystemSenderDomain` — a custom sender on
 * `sg.curium.app` (an infra subdomain, not an authenticated sender) flows
 * through to authenticatedDomain just like any other custom sender.
 */
export function resolveEffectiveAuthenticatedDomain(
    senderEmail: string | null | undefined,
    authenticatedDomain: string | null | undefined,
): string | null {
    const resolvedEmail = senderEmail || EMAIL_SETTINGS.DEFAULT_FROM.email;
    const domain = resolvedEmail.split('@')[1];
    if (domain && isSystemSenderDomain(domain)) {
        return null;
    }
    return authenticatedDomain ?? null;
}

export type NoReplyEmailDomainRuleFailure =
    | 'no-effective-authenticated-domain'
    | 'authenticated-domain-not-verified'
    | 'domain-mismatch';

export type NoReplyEmailDomainRuleResult =
    | { valid: true }
    | {
          valid: false;
          reason: NoReplyEmailDomainRuleFailure;
          effectiveAuthenticatedDomain: string | null;
      };

interface CheckNoReplyEmailDomainRuleInput {
    noReplyEmail: string | null | undefined;
    previousNoReplyEmail: string | null | undefined;
    senderEmail: string | null | undefined;
    previousSenderEmail: string | null | undefined;
    authenticatedDomain: string | null | undefined;
    isAuthenticatedDomainVerified: boolean;
}

function normaliseOptionalEmail(value: string | null | undefined): string | null {
    return value?.trim().toLowerCase() || null;
}

/**
 * Browser-safe no-reply ownership predicate shared by the form and server write boundaries.
 *
 * Blank values use the platform default. An unchanged persisted value is grandfathered only
 * while its effective sender-domain context is unchanged; changing from a custom sender to a
 * system sender therefore requires the no-reply address to be cleared. Every new, changed, or
 * newly applicable custom value requires completed domain authentication and an exact domain
 * match.
 */
export function checkNoReplyEmailDomainRule({
    noReplyEmail,
    previousNoReplyEmail,
    senderEmail,
    previousSenderEmail,
    authenticatedDomain,
    isAuthenticatedDomainVerified,
}: CheckNoReplyEmailDomainRuleInput): NoReplyEmailDomainRuleResult {
    const normalisedNoReplyEmail = normaliseOptionalEmail(noReplyEmail);
    if (!normalisedNoReplyEmail) {
        return { valid: true };
    }

    const effectiveAuthenticatedDomain = resolveEffectiveAuthenticatedDomain(senderEmail, authenticatedDomain);
    const previousEffectiveAuthenticatedDomain = resolveEffectiveAuthenticatedDomain(
        previousSenderEmail,
        authenticatedDomain,
    );
    const isNoReplyEmailUnchanged = normalisedNoReplyEmail === normaliseOptionalEmail(previousNoReplyEmail);
    const isEffectiveDomainContextUnchanged =
        normaliseOptionalEmail(effectiveAuthenticatedDomain) ===
        normaliseOptionalEmail(previousEffectiveAuthenticatedDomain);

    if (isNoReplyEmailUnchanged && isEffectiveDomainContextUnchanged) {
        return { valid: true };
    }

    if (!effectiveAuthenticatedDomain) {
        return {
            valid: false,
            reason: 'no-effective-authenticated-domain',
            effectiveAuthenticatedDomain,
        };
    }

    if (!isAuthenticatedDomainVerified) {
        return {
            valid: false,
            reason: 'authenticated-domain-not-verified',
            effectiveAuthenticatedDomain,
        };
    }

    const noReplyDomain = normalisedNoReplyEmail.split('@')[1];
    if (noReplyDomain !== effectiveAuthenticatedDomain.toLowerCase()) {
        return {
            valid: false,
            reason: 'domain-mismatch',
            effectiveAuthenticatedDomain,
        };
    }

    return { valid: true };
}

/**
 * An org's `emailId` doubles as (1) the case-insensitive AI-inbox lookup key and (2) the local part
 * of Curium-managed inbound addresses (`<emailId>@<SYSTEM_INBOUND_DOMAIN>` reply-to and
 * `<emailId>@<AI_INBOX_DOMAIN>` AI inbox). Because it can land verbatim in an outbound `Reply-To`
 * header, it must be a safe email local part: lowercase alphanumerics joined by single `.`, `-` or
 * `_` separators, with no leading/trailing or doubled separators. RFC 5321 caps a local part at 64
 * characters. Shared so the admin form's client-side validation is byte-identical to the server
 * boundary (`inboundEmailIdInputSchema`).
 */
export const INBOUND_EMAIL_ID_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

/** Whether an (already trimmed/lower-cased) value is a safe email local part for managed addresses. */
export function isValidInboundEmailId(value: string): boolean {
    return value.length <= 64 && INBOUND_EMAIL_ID_PATTERN.test(value);
}
